import * as echarts from 'echarts';
import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { DASHBOARD_COLORS } from '../../../../../../../constants/DashboardColors';

interface ChartItem {
  label: string;
  value: number;
}

interface PieChartProps {
  data: ChartItem[];
  title?: string;
  colors?: string[];
}

export const PieChart: React.FC<PieChartProps> = ({
  data,
  title = 'Invitation Status',
  colors = DASHBOARD_COLORS,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const totalValue = data.reduce((sum, item) => sum + item.value, 0);
  const filteredData = data.filter((item) => item.value > 0);

  useEffect(() => {
    if (!chartRef.current || filteredData.length === 0) return;
    const myChart = echarts.init(chartRef.current);

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        textStyle: { color: '#1e293b' },
        borderWidth: 0,
        extraCssText:
          'box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1); border-radius: 8px;',
      },
      graphic: {
        type: 'text',
        left: 'center',
        top: 'center',
        style: {
          text: totalValue.toString(),
          fill: '#1e293b',
          fontSize: 22,
          fontWeight: 'bold',
        },
      },
      series: [
        {
          name: title,
          type: 'pie',
          radius: ['65%', '85%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: false,
          label: { show: false },
          emphasis: { label: { show: false } },
          data: filteredData.map((item, index) => ({
            value: item.value,
            name: item.label,
            itemStyle: {
              color: colors[index % colors.length],
            },
          })),
        },
      ],
    };

    myChart.setOption(option);

    const handleResize = () => myChart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      myChart.dispose();
    };
  }, [filteredData, title, colors, totalValue]);

  return (
    <Paper
      elevation={1}
      sx={{
        display: 'flex',
        height: '100%',
        minHeight: { xs: 160, sm: 190, lg: 220 },
        width: '100%',
        flexDirection: 'column',
        borderRadius: 2,
        p: 2,
        transition: 'all 0.2s',
        '&:hover': { boxShadow: 3 },
      }}
    >
      <Box sx={{ mb: 1.5, display: 'flex', flexShrink: 0, alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {title}
        </Typography>
      </Box>

      {totalValue > 0 ? (
        <Box sx={{ display: 'flex', minHeight: 0, flex: 1, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 1.5 }}>
          <Box sx={{ position: 'relative', height: { xs: 140, sm: '100%' }, width: { xs: '100%', sm: '58%' } }}>
            <Box ref={chartRef} sx={{ width: '100%', height: '100%' }} />
          </Box>

          <Box sx={{ display: 'flex', maxHeight: '100%', width: { xs: '100%', sm: '42%' }, flexDirection: 'column', justifyContent: 'center', gap: 0.5, overflowY: 'auto', pr: 0.5 }}>
            {filteredData.map((entry, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: 0.25,
                }}
              >
                <Box sx={{ display: 'flex', maxWidth: '75%', alignItems: 'center', gap: 1 }}>
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      flexShrink: 0,
                      bgcolor: colors[index % colors.length],
                    }}
                  />
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{
                      fontSize: '11px',
                      fontWeight: 600,
                      color: 'text.secondary',
                      transition: 'color 0.2s',
                      '&:hover': { color: 'text.primary' },
                    }}
                  >
                    {entry.label}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary', flexShrink: 0 }}>
                  {entry.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            minHeight: 92,
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 1.5,
            borderStyle: 'dashed',
            bgcolor: 'action.hover',
            px: 2,
            textAlign: 'center',
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block' }}>
              No data yet
            </Typography>
            <Typography variant="caption" sx={{ fontSize: '11px', color: 'text.disabled', mt: 0.5, display: 'block' }}>
              Metrics will appear after interviews are available.
            </Typography>
          </Box>
        </Paper>
      )}
    </Paper>
  );
};

export default PieChart;
